/**
 * Ledger Entry Row Component
 * Displays a single ledger entry with appropriate styling.
 * Payment-type entries show a payment status badge (similar to the tenant app).
 */

import { type LedgerEntry } from '../../lib/api';
import { formatCalendarDate, formatTimestamp } from '@resida/shared';
import { usePropertyTimezone } from '../../hooks/usePropertyTimezone';

interface LedgerEntryRowProps {
  entry: LedgerEntry;
}

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));


const ENTRY_ICONS: Record<string, string> = {
  charge: '📋',
  payment: '💵',
  credit: '🎁',
  reversal: '↩️',
  adjustment: '📝',
};

const AMOUNT_COLOR: Record<string, string> = {
  charge: 'text-destructive',
  reversal: 'text-destructive',
  payment: 'text-success',
  credit: 'text-success',
  adjustment: 'text-muted-foreground',
};

/**
 * Payment status badge config.
 * Ledger entries of type 'payment' represent settled payments.
 * Ledger entries of type 'reversal' represent returned/failed payments.
 */
const PAYMENT_STATUS: Record<string, { label: string; className: string } | undefined> = {
  payment: {
    label: 'Completed',
    className: 'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-success/10 text-success',
  },
  reversal: {
    label: 'Returned',
    className: 'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-destructive/5 text-destructive',
  },
};

const DATE_DISPLAY_OPTIONS: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

export function LedgerEntryRow({ entry }: LedgerEntryRowProps) {
  const timezone = usePropertyTimezone();
  const icon = ENTRY_ICONS[entry.entryType] ?? '•';
  const amountColor = AMOUNT_COLOR[entry.entryType] ?? 'text-foreground';
  const isDebit = entry.entryType === 'charge' || entry.entryType === 'reversal';
  const statusBadge = PAYMENT_STATUS[entry.entryType];

  return (
    <div
      data-testid={`ledger-entry-${entry.id}`}
      className="bg-card rounded-lg border border-border p-4 flex justify-between items-center"
    >
      <div className="flex gap-3 flex-1">
        <div
          className="text-2xl w-10 h-10 flex items-center justify-center shrink-0"
          aria-hidden="true"
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-base font-semibold m-0 text-foreground truncate">
              {entry.description}
            </h4>
            {statusBadge && (
              <span
                className={statusBadge.className}
                data-testid={`entry-payment-status-${entry.id}`}
              >
                {statusBadge.label}
              </span>
            )}
          </div>
          <div className="flex gap-2 mt-1 flex-wrap">
            <p className="text-sm text-muted-foreground m-0">
              {entry.createdAt ? formatTimestamp(entry.createdAt, timezone, DATE_DISPLAY_OPTIONS) : ''}
            </p>
            {entry.dueDate && (
              <>
                <span className="text-muted-foreground">•</span>
                <p className="text-sm text-muted-foreground m-0">
                  Due: {formatCalendarDate(entry.dueDate, DATE_DISPLAY_OPTIONS)}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="text-right shrink-0 ml-4">
        <p
          data-testid={`entry-amount-${entry.id}`}
          className={`text-lg font-semibold m-0 ${amountColor}`}
        >
          {isDebit ? '+' : '−'}
          {formatCurrency(entry.amount / 100)}
        </p>
        <p
          data-testid={`entry-balance-${entry.id}`}
          className="text-sm text-muted-foreground mt-1 m-0"
        >
          Balance: {formatCurrency(entry.balanceAfter / 100)}
        </p>
      </div>
    </div>
  );
}
